import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAllDoctors, getDoctorById, createBooking } from '../../api';
import PaymentGateway from '../../components/PaymentGateway';
import styles from './BookAppointment.module.css';

const BookAppointment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const doctorIdFromUrl = searchParams.get('doctorId');

  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);

  const [formData, setFormData] = useState({
    doctor: doctorIdFromUrl || '',
    date: '',
    slot: '',
    patientName: '',
    phoneNumber: '',
    email: '',
    reason: ''
  });

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoading(true);
        const data = await getAllDoctors();
        const validDoctors = Array.isArray(data) ? data.filter(doctor => doctor && typeof doctor === 'object') : [];
        setDoctors(validDoctors);

        // If doctorId is provided in URL, set it as selected
        if (doctorIdFromUrl) {
          const doctor = validDoctors.find(d => d._id === doctorIdFromUrl);
          if (doctor) {
            setSelectedDoctor(doctor);
            setFormData(prev => ({ ...prev, doctor: doctorIdFromUrl }));
          }
        }

        setError(null);
      } catch (err) {
        setError('Failed to load doctors');
        console.error('Error fetching doctors:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, [doctorIdFromUrl]);

  const handleBack = () => {
    navigate('/user/doctors');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // If doctor is changed, update selected doctor
    if (name === 'doctor') {
      const doctor = doctors.find(d => d._id === value);
      setSelectedDoctor(doctor);
    }
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 17; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const nextMinute = minute + 30;
        const nextHour = nextMinute >= 60 ? hour + 1 : hour;
        const nextTime = `${nextHour.toString().padStart(2, '0')}:${(nextMinute % 60).toString().padStart(2, '0')}`;
        slots.push(`${time} - ${nextTime}`);
      }
    }
    return slots;
  };

  const validateForm = () => {
    if (!formData.doctor) {
      setError('Please select a doctor');
      return false;
    }
    if (!formData.date) {
      setError('Please select a date');
      return false;
    }
    if (!formData.slot) {
      setError('Please select a time slot');
      return false;
    }
    if (!formData.patientName.trim()) {
      setError('Please enter patient name');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      setError('Please enter phone number');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Please enter email address');
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Validate date is not in the past
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      setError('Please select a future date');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const bookingData = {
        doctor: formData.doctor,
        date: formData.date,
        slot: formData.slot,
        fees: selectedDoctor?.fees || 0,
        patientName: formData.patientName,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        reason: formData.reason,
        status: 'Pending', // Changed to Pending until payment is completed
        paymentStatus: 'Pending'
      };

      console.log('Creating pending booking:', bookingData);

      const response = await createBooking(bookingData);
      console.log('Booking response:', response);

      // Store the booking and show payment gateway
      setPendingBooking(response);
      setShowPayment(true);

    } catch (err) {
      console.error('Booking error:', err);
      setError(err.message || 'Failed to create booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = (booking) => {
    setSuccess('Payment successful! Appointment booked successfully! Redirecting to booking history...');
    setShowPayment(false);
    setPendingBooking(null);

    // Reset form
    setFormData({
      doctor: doctorIdFromUrl || '',
      date: '',
      slot: '',
      patientName: '',
      phoneNumber: '',
      email: '',
      reason: ''
    });

    // Redirect to booking history after 2 seconds
    setTimeout(() => {
      navigate('/user/booking-history');
    }, 2000);
  };

  const handlePaymentFailure = (error) => {
    setError(`Payment failed: ${error}. Please try again.`);
    setShowPayment(false);
  };

  const handlePaymentClose = () => {
    setShowPayment(false);
    setPendingBooking(null);
  };

  const timeSlots = generateTimeSlots();

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.loadingContainer}>
            <h2 className={styles.loadingText}>Loading booking form...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <button
            onClick={handleBack}
            className={styles.backButton}
          >
            ← Back to Doctors
          </button>
          <h1 className={styles.title}>Book an Appointment</h1>
        </div>

        <div className={styles.mainContent}>
          {/* Success/Error Messages */}
          {success && (
            <div className={styles.successMessage}>
              {success}
            </div>
          )}

          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {/* Booking Form */}
          <div className={styles.formContainer}>
            <h2 className={styles.formTitle}>
              Appointment Details
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Doctor Selection */}
              <div className={styles.formGroup}>
                <label className={`${styles.label} ${styles.required}`}>
                  Select Doctor
                </label>
                <select
                  name="doctor"
                  value={formData.doctor}
                  onChange={handleInputChange}
                  className={styles.select}
                  required
                >
                  <option value="">Choose a doctor...</option>
                  {doctors.map((doctor) => (
                    <option key={doctor._id} value={doctor._id}>
                      Dr. {doctor.name} - {doctor.designationDegree} (${doctor.fees})
                    </option>
                  ))}
                </select>
              </div>

              {/* Selected Doctor Info */}
              {selectedDoctor && (
                <div className={styles.doctorInfo}>
                  <h4>Selected Doctor:</h4>
                  <p>
                    <strong>Name:</strong> Dr. {selectedDoctor.name}
                  </p>
                  <p>
                    <strong>Specialization:</strong> {selectedDoctor.designationDegree}
                  </p>
                  <p>
                    <strong>Experience:</strong> {selectedDoctor.experience} years
                  </p>
                  <p>
                    <strong>Fees:</strong> ${selectedDoctor.fees}
                  </p>
                  <p className={selectedDoctor.slotAvailability ? styles.availabilityAvailable : styles.availabilityUnavailable}>
                    <strong>Availability:</strong> {selectedDoctor.slotAvailability ? 'Available for booking' : 'Currently not available'}
                  </p>
                </div>
              )}

              {/* Date and Time Selection */}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.required}`}>
                    Appointment Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.required}`}>
                    Time Slot
                  </label>
                  <select
                    name="slot"
                    value={formData.slot}
                    onChange={handleInputChange}
                    className={styles.select}
                    required
                  >
                    <option value="">Select time slot...</option>
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Patient Information */}
              <div className={styles.formGroup}>
                <h3 className={styles.sectionTitle}>Patient Information</h3>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={`${styles.label} ${styles.required}`}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="patientName"
                      value={formData.patientName}
                      onChange={handleInputChange}
                      placeholder="Enter patient's full name"
                      className={styles.input}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={`${styles.label} ${styles.required}`}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                      className={styles.input}
                      required
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.required}`}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    className={styles.input}
                    required
                  />
                </div>
              </div>

              {/* Reason for Visit */}
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Reason for Visit
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  placeholder="Please describe the reason for your visit (optional)"
                  rows="4"
                  className={styles.textarea}
                />
              </div>

              {/* Submit Button */}
              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  onClick={handleBack}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !selectedDoctor?.slotAvailability}
                  className={styles.submitButton}
                >
                  {submitting ? 'Creating Booking...' : 'Proceed to Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Payment Gateway Modal */}
      {showPayment && pendingBooking && (
        <PaymentGateway
          amount={pendingBooking.fees}
          bookingId={pendingBooking._id}
          patientName={pendingBooking.patientName}
          doctorName={selectedDoctor ? `Dr. ${selectedDoctor.name}` : 'Doctor'}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentFailure={handlePaymentFailure}
          onClose={handlePaymentClose}
        />
      )}
    </div>
  );
};

export default BookAppointment; 